import { useMemo } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { msg, T, useGT, Var } from 'gt-react';
import type { ProcSavedReport, WorkbookDetail } from '@erp/rpc';
import {
  serializeProcSavedReport,
  type ReportCanvasLayout,
  type SavedReportSource,
  type WorkbookDefinition,
  type WorkbookRun,
} from '@erp/shared-types';
import Page from '@/components/page/Page';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { orpc } from '@/api/rpc/client';
import { useProcSavedReportsList } from '@/api/rpc/hooks/procurement/useProcSavedReports';
import { useCan } from '@/hooks/useCan';
import { useWorkbookReportData } from '../useWorkbookReportData';
import { WorkbookReportCanvas } from '../WorkbookReportCanvas';
import { Route as parent, requirePermission } from '../../route';

type WorkbookSource = Extract<SavedReportSource, { kind: 'workbook' }>;

/** Saves a library report's changes in order, so the last change is the one kept. */
function useSaveLibraryReport(saved: ProcSavedReport, source: WorkbookSource) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const update = useMutation(
    orpc.procurement.savedReports.update.mutationOptions({
      scope: { id: `saved-report-${saved.id}` },
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.procurement.savedReports.list.key(),
        }),
      onError: () =>
        toast.error(
          gt('Could not save the report.', {
            context: 'workbook report save to library failed',
          }),
        ),
    }),
  );
  return (next: { source?: WorkbookSource; layout?: ReportCanvasLayout }) =>
    update.mutate({
      id: saved.id,
      report: serializeProcSavedReport({
        source: next.source ?? source,
        report: next.layout ?? saved.report.report,
      }),
    });
}

/** Which Workbook and Worksheet a library report charts; editors can choose another Worksheet. */
function LibraryReportSource({
  workbook,
  sheetId,
  onSheet,
}: {
  workbook: WorkbookDetail;
  sheetId: string;
  /** Absent for viewers who cannot edit the report. */
  onSheet?: (sheetId: string) => void;
}) {
  const gt = useGT();
  const sheet = workbook.definition.sheets.find((item) => item.id === sheetId);
  return (
    <span className="flex flex-wrap items-center gap-2">
      <T context="workbook library report source">
        From{' '}
        <Link
          to="/workbooks/$workbookId"
          params={{ workbookId: workbook.id }}
          className="underline"
        >
          <Var>{workbook.name}</Var>
        </Link>
      </T>
      {onSheet ? (
        <Select value={sheetId} onValueChange={onSheet}>
          <SelectTrigger
            className="h-7 w-48 text-xs"
            aria-label={gt('Worksheet', {
              context: 'workbook library report worksheet',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {workbook.definition.sheets.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        sheet && <span>· {sheet.name}</span>
      )}
    </span>
  );
}

/** A library report charting one Worksheet's newest Current Result. */
function WorkbookLibraryReport({
  saved,
  source,
}: {
  saved: ProcSavedReport;
  source: WorkbookSource;
}) {
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const input = { workbookId: source.workbookId };
  const workbook = useQuery(orpc.workbooks.get.queryOptions({ input }));
  const runs = useQuery(orpc.workbooks.runs.queryOptions({ input }));
  const save = useSaveLibraryReport(saved, source);
  const definition = workbook.data?.definition;
  const charted = definition?.sheets.some((item) => item.id === source.sheetId);
  return (
    <Page
      title={saved.name}
      subtitle={
        workbook.data && (
          <LibraryReportSource
            workbook={workbook.data}
            sheetId={source.sheetId}
            onSheet={
              canUpdate
                ? (sheetId) => save({ source: { ...source, sheetId } })
                : undefined
            }
          />
        )
      }
      isLoading={workbook.isPending}
      error={workbook.error ?? undefined}
    >
      {definition &&
        (charted ? (
          <SheetReport
            workbookId={source.workbookId}
            sheetId={source.sheetId}
            definition={definition}
            runs={runs.data}
            layout={saved.report.report}
            onChange={canUpdate ? (layout) => save({ layout }) : undefined}
          />
        ) : (
          <p role="status" className="py-8 text-sm text-muted-foreground">
            <T context="workbook library report worksheet removed">
              This report's worksheet was removed from the workbook. Choose
              another worksheet to chart.
            </T>
          </p>
        ))}
    </Page>
  );
}

/** The Report canvas over one Worksheet's Report dataset, read by every component. */
function SheetReport({
  workbookId,
  sheetId,
  definition,
  runs,
  layout,
  onChange,
}: {
  workbookId: string;
  sheetId: string;
  definition: WorkbookDefinition;
  runs: WorkbookRun[] | undefined;
  layout: ReportCanvasLayout;
  onChange?: (layout: ReportCanvasLayout) => void;
}) {
  const keys = useMemo(() => new Set([sheetId]), [sheetId]);
  const data = useWorkbookReportData({ workbookId, definition, runs, keys });
  const sheetData = useMemo(
    () => ({
      ...data,
      datasets: data.datasets.filter((dataset) => dataset.key === sheetId),
      defaultDatasetKey: sheetId,
    }),
    [data, sheetId],
  );
  return (
    <WorkbookReportCanvas
      layout={layout}
      data={sheetData}
      definition={definition}
      onChange={onChange}
    />
  );
}

function WorkbookLibraryReportPage() {
  const { reportId } = Route.useParams();
  const { reports, isPending, error } = useProcSavedReportsList();
  const saved = reports.find((report) => report.id === reportId);
  const source = saved?.report.source;
  if (saved && source?.kind === 'workbook')
    return <WorkbookLibraryReport saved={saved} source={source} />;
  return (
    <Page isLoading={isPending} error={error ?? undefined}>
      <p role="status" className="py-8 text-sm text-muted-foreground">
        {saved ? (
          <T context="workbook library report charts a query">
            This report charts a query. Open it in the{' '}
            <Link to="/procurement/query-builder" className="underline">
              query builder
            </Link>
            .
          </T>
        ) : (
          <T context="workbook library report not found">
            This report doesn't exist, or it isn't yours.
          </T>
        )}
      </p>
    </Page>
  );
}

export const Route = createRoute({
  getParentRoute: () => parent,
  path: '/workbooks/reports/$reportId',
  component: WorkbookLibraryReportPage,
  loader: () => ({
    crumb: msg('Report', { context: 'workbook library report breadcrumb' }),
  }),
  beforeLoad: async ({ context }) =>
    requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Workbook,
    }),
});
